// Admin modal for creating/editing PEME crew status records
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Search, Loader2 } from 'lucide-react';
import { debounce } from 'lodash';

import EmployeesApi from 'api/employees';
import { PIPELINE_STAGES } from '../constants/peme-stages';

const PEME_STATUSES = ['Fit to Work', 'In Clinic', 'Pending', 'Pending Scheduling', 'Temporarily Unfit'];

const PemeCrewStatusModal = ({ open, onClose, editingCrew, onSave }) => {
  // Form state
  const [employeeId, setEmployeeId] = useState('');
  const [stage, setStage] = useState(PIPELINE_STAGES[0]);
  const [status, setStatus] = useState(PEME_STATUSES[2]); // Default: Pending
  const [isSaving, setIsSaving] = useState(false);

  // Employee search state
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const isEditMode = !!editingCrew;

  // Pre-fill form when editing
  useEffect(() => {
    if (editingCrew) {
      setStage(editingCrew.stage || PIPELINE_STAGES[0]);
      setStatus(editingCrew.status || PEME_STATUSES[2]);
      setEmployeeId('');
      setEmployeeSearch('');
    } else {
      setStage(PIPELINE_STAGES[0]);
      setStatus(PEME_STATUSES[2]);
      setEmployeeId('');
      setEmployeeSearch('');
      setEmployeeOptions([]);
    }
  }, [editingCrew, open]);

  // Debounced employee search
  const searchEmployees = useCallback(
    debounce(async (query) => {
      if (!query || query.length < 2) {
        setEmployeeOptions([]);
        setIsSearching(false);
        return;
      }
      try {
        setIsSearching(true);
        const res = await EmployeesApi.getAll({ search: query, limit: 20, offset: 0, filters: { isDeleted: false } });
        setEmployeeOptions(res?.data?.data || []);
      } catch {
        setEmployeeOptions([]);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    return () => searchEmployees.cancel();
  }, [searchEmployees]);

  const handleEmployeeSearchChange = (e) => {
    const value = e.target.value;
    setEmployeeSearch(value);
    setEmployeeId('');
    setShowDropdown(true);
    searchEmployees(value);
  };

  const selectEmployee = (emp) => {
    setEmployeeId(emp._id);
    setEmployeeSearch(`${emp.firstName || ''} ${emp.lastName || ''}`.trim());
    setShowDropdown(false);
  };

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDropdown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = isEditMode ? { stage, status } : { employeeId, stage, status };
    try {
      setIsSaving(true);
      await onSave(payload);
    } finally {
      setIsSaving(false);
    }
  };

  const canSubmit = isEditMode ? true : !!employeeId;

  if (!open) return null;

  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center">
      <div className="tw-fixed tw-inset-0 tw-bg-black/50" onClick={isSaving ? undefined : onClose} />
      <div className="tw-relative tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-md tw-mx-4">
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b tw-border-gray-200">
          <h2 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-m-0">
            {isEditMode ? 'Edit PEME Record' : 'Create PEME Record'}
          </h2>
          <button onClick={onClose} className="tw-p-1 tw-rounded tw-border-0 tw-bg-transparent tw-cursor-pointer hover:tw-bg-gray-100">
            <X className="tw-w-5 tw-h-5 tw-text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="tw-p-6 tw-space-y-4">
          {/* Employee search (create mode only) */}
          {!isEditMode && (
            <div ref={dropdownRef} className="tw-relative">
              <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">Employee *</label>
              <div className="tw-relative">
                <Search className="tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-w-4 tw-h-4 tw-text-gray-400" />
                <input
                  type="text"
                  value={employeeSearch}
                  onChange={handleEmployeeSearchChange}
                  onFocus={() => employeeOptions.length > 0 && setShowDropdown(true)}
                  placeholder="Search by name or email..."
                  className="tw-w-full tw-pl-10 tw-pr-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500 focus:tw-border-transparent"
                />
                {isSearching && <Loader2 className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-w-4 tw-h-4 tw-text-gray-400 tw-animate-spin" />}
              </div>
              {showDropdown && !isSearching && employeeSearch.length >= 2 && (
                <div className="tw-absolute tw-z-10 tw-w-full tw-mt-1 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-lg tw-max-h-48 tw-overflow-y-auto">
                  {employeeOptions.length > 0 ? employeeOptions.map((emp) => (
                    <button
                      key={emp._id}
                      type="button"
                      onClick={() => selectEmployee(emp)}
                      className="tw-w-full tw-text-left tw-px-3 tw-py-2 tw-text-sm hover:tw-bg-indigo-50 tw-bg-transparent tw-border-0 tw-cursor-pointer"
                    >
                      <span className="tw-font-medium tw-text-gray-900">{`${emp.firstName || ''} ${emp.lastName || ''}`.trim()}</span>
                      <span className="tw-text-gray-500 tw-ml-2">{emp.email}</span>
                    </button>
                  )) : (
                    <div className="tw-px-3 tw-py-2 tw-text-sm tw-text-gray-400">No employees found</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Edit mode: show crew name as context */}
          {isEditMode && (
            <div>
              <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">Crew Member</label>
              <p className="tw-text-sm tw-text-gray-900 tw-m-0 tw-py-2">{editingCrew.name}</p>
            </div>
          )}

          {/* Stage dropdown */}
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">Stage *</label>
            <select
              value={stage}
              onChange={(e) => setStage(e.target.value)}
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500 focus:tw-border-transparent"
            >
              {PIPELINE_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Status dropdown */}
          <div>
            <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">Status *</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-lg tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500 focus:tw-border-transparent"
            >
              {PEME_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Footer buttons */}
          <div className="tw-flex tw-justify-end tw-gap-3 tw-pt-2">
            <button
              type="button"
              onClick={onClose}
              className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg hover:tw-bg-gray-50 tw-cursor-pointer tw-transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit || isSaving}
              className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-indigo-600 tw-border-0 tw-rounded-lg hover:tw-bg-indigo-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-cursor-pointer tw-transition-colors tw-flex tw-items-center tw-gap-2"
            >
              {isSaving && <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />}
              {isEditMode ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PemeCrewStatusModal;
